/**
 * Registra a visita à página pública.
 *
 * Uma vez por aba (`sessionStorage`): recarregar a página ou voltar do login
 * não pode inflar o contador, senão o número deixa de responder "quantas
 * pessoas chegaram" e passa a medir quantas vezes alguém apertou F5.
 *
 * `keepalive` para o registro sobreviver à navegação imediata — quem chega e
 * clica em "criar conta" no mesmo segundo é exatamente a visita que mais
 * interessa contar.
 *
 * Falha em silêncio: nenhum contador vale um erro na cara de quem acabou de
 * chegar pelo anúncio.
 */
import { podeMedirAudiencia } from './consentimento';

const CHAVE = 'bx_visita';
/** Primeiro canal que trouxe a pessoa — enviado junto com o cadastro. */
const CHAVE_ORIGEM = 'bx_origem'; // apagado em consentimento.ts ao recusar

/** Canal de aquisição, no formato que POST /auth/registrar aceita. */
export interface Origem {
  /** Vazio = veio direto (digitou o endereço, app, navegador sem referrer). */
  referrer: string;
  utmSource?: string;
  utmMedium?: string;
}

export function registrarVisita(): void {
  // Sem consentimento, nada é medido — nem enquanto a pessoa não decidiu.
  // Contar antes da escolha transformaria o aviso de cookies num enfeite.
  if (!podeMedirAudiencia()) return;

  try {
    if (sessionStorage.getItem(CHAVE)) return;
    sessionStorage.setItem(CHAVE, '1');
  } catch {
    // Navegador com armazenamento bloqueado: conta a visita mesmo assim, que é
    // melhor do que perdê-la.
  }

  const params = new URLSearchParams(window.location.search);
  guardarPrimeiraOrigem(params);
  void fetch('/api/metricas/visita', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      caminho: window.location.pathname,
      referrer: document.referrer || undefined,
      utmSource: params.get('utm_source') ?? undefined,
      utmMedium: params.get('utm_medium') ?? undefined,
    }),
  }).catch(() => undefined);
}

/**
 * Guarda o PRIMEIRO canal (first-touch): quem chegou pelo Google e voltou dias
 * depois digitando o endereço foi trazido pelo Google, não pelo "direto".
 *
 * Navegação dentro do próprio site não é canal: o referrer de quem volta da
 * página de privacidade para a home é o nosso domínio.
 */
function guardarPrimeiraOrigem(params: URLSearchParams): void {
  try {
    if (localStorage.getItem(CHAVE_ORIGEM)) return;
    let referrer = document.referrer || '';
    if (referrer && new URL(referrer).host === window.location.host) {
      referrer = '';
    }
    const origem: Origem = {
      referrer,
      utmSource: params.get('utm_source') ?? undefined,
      utmMedium: params.get('utm_medium') ?? undefined,
    };
    localStorage.setItem(CHAVE_ORIGEM, JSON.stringify(origem));
  } catch {
    // Armazenamento bloqueado ou referrer malformado: o cadastro segue sem canal.
  }
}

/**
 * Canal a enviar no cadastro, ou `undefined` se não há consentimento ou nada
 * foi guardado — e aí o painel mostra "não informado", que é a verdade.
 */
export function origemDoCadastro(): Origem | undefined {
  if (!podeMedirAudiencia()) return undefined;
  try {
    const bruto = localStorage.getItem(CHAVE_ORIGEM);
    if (!bruto) return undefined;
    const o = JSON.parse(bruto) as Partial<Origem>;
    return {
      referrer: typeof o.referrer === 'string' ? o.referrer.slice(0, 500) : '',
      utmSource: o.utmSource?.slice(0, 60) || undefined,
      utmMedium: o.utmMedium?.slice(0, 60) || undefined,
    };
  } catch {
    return undefined;
  }
}
